export function formatCurrencyAsIs(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
  }).format(value);
}

export function generateColorPair(
  str: string,
  mode: 'light' | 'dark' = 'light'
) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = String(Math.abs(hash) % 360);
  const saturation = String(40 + (Math.abs(hash) % 40)); // 40–80%
  const lightness = 40 + (Math.abs(hash >> 3) % 20); // 40–60%

  const bgColor = `hsl(${hue}, ${saturation}%, ${String(lightness + 40)}%)`; // pastel
  const textColor = `hsl(${hue}, ${saturation}%, ${String(lightness - 30)}%)`; // darker

  if (mode === 'dark') {
    return {
      bgColor: textColor,
      textColor: bgColor,
    };
  }

  return { bgColor, textColor };
}

export function getStringInitials(
  str: string | null | undefined,
  regex = /[\s-]+/
): string {
  if (!str?.trim()) {
    return '-';
  }

  const words = str.trim().split(regex);
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export function isIframeWindow() {
  return window.self !== window.top;
}

export function isAllowedHost(
  hostUrl: string,
  allowedHosts: string[],
  allowSubdomains = true
) {
  const url = new URL(hostUrl);

  return allowedHosts.some((allowedHost) => {
    if (allowedHost === '*') {
      return true;
    }

    if (allowSubdomains) {
      return (
        url.hostname === allowedHost || url.hostname.endsWith(`.${allowedHost}`)
      );
    }

    return url.hostname === allowedHost;
  });
}

export function hexToHsl(hex: string) {
  const colorCode = hex.replace('#', '');

  // expand shorthand form (e.g. "03F", "03F5") to full form (e.g. "0033FF", "0033FF55")
  const fullHex =
    colorCode.length === 3 || colorCode.length === 4
      ? colorCode
          .split('')
          .map((c) => c + c)
          .join('')
      : colorCode;

  // remove alpha if present
  const hexWithoutAlpha = fullHex.length === 8 ? fullHex.slice(0, 6) : fullHex;

  if (hexWithoutAlpha.length !== 6) {
    throw new Error('Invalid HEX color');
  }

  // convert hex to RGB
  const r = parseInt(hexWithoutAlpha.slice(0, 2), 16) / 255;
  const g = parseInt(hexWithoutAlpha.slice(2, 4), 16) / 255;
  const b = parseInt(hexWithoutAlpha.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  // Calculate hue
  if (delta !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      case b:
        h = (r - g) / delta + 4;
        break;
    }
    h = Math.round(h * 60);
    if (h < 0) {
      h += 360;
    }
  }

  // Calculate Saturation
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
  }

  return {
    h,
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}
